import {
  ChangeEvent,
  ClipboardEvent,
  createRef,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { useHelpTextId } from '../../../hooks/useHelpTextId'
import { Flex } from '../../layout/Flex'
import { Grid } from '../../layout/Grid'
import { VARIANTS } from '../Input/arcade-input-styles'
import ArcadeHelpText, { ArcadeHelpTextComponentProps, extractHelpTextProps } from '../Input/ArcadeHelpText'
import { default as ArcadeInput } from '../Input/ArcadeInput'
import { ProgressCircular } from '../ProgressCircular/ProgressCircular'

const ANIMATION_DURATION = 300
const shakeAnimation = keyframes`
  0% { transform: translateX(-10px) }
  16% { transform: translateX(9px) }
  33% { transform: translateX(-6px) }
  50% { transform: translateX(5px) }
  66% { transform: translateX(-2px) }
  83% { transform: translateX(1px) }
  100% { transform: translateX(0px) }
`

const StyledInput = styled(ArcadeInput)<{ hasError: boolean; disabled: boolean }>`
  width: 3.25rem;
  height: 4rem;

  ::selection {
    background: transparent;
  }

  ${({ disabled, hasError, theme }) =>
    hasError &&
    css`
      animation: ${shakeAnimation} ${ANIMATION_DURATION}ms linear;
      color: ${theme.arcadeColors.component[VARIANTS['invalid'].color]};
      background: ${theme.arcadeColors.component[VARIANTS['invalid'].background]};
      ${!disabled && `border-color: ${theme.arcadeColors.component[VARIANTS['invalid'].border]};`}
    `}

  && {
    color: transparent;
    caret-color: transparent;
  }
`

const Circle = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: ${({ theme }) => theme.border.radius.circle};
  position: absolute;
  background: ${({ theme }) => theme.arcadeColors.component.inputTextDefault};
  inset: 0 0 0 0;
  margin: auto;
`

const PinInputContainer = styled.div`
  position: relative;

  :not(:last-child) {
    margin-right: ${({ theme }) => theme.spacing.xs};
  }
`

export type PinInputProps = Pick<React.InputHTMLAttributes<HTMLInputElement>, 'disabled' | 'value'> &
  ArcadeHelpTextComponentProps & {
    name: string
    pinLength: number
    ariaLabel: string
    onComplete?: (pin: string) => void
    onChange?: (value: string) => void
    isLoading?: boolean
  }

type PinInputBoxProps = Pick<React.InputHTMLAttributes<HTMLInputElement>, 'disabled'> & {
  index: number
  pin: Array<string>
  inputRefs: Array<RefObject<HTMLInputElement>>
  autoTab: (event: KeyboardEvent, index: number) => void
  handleChange: (event: ChangeEvent<HTMLInputElement>, index: number) => void
  handlePaste: (event: ClipboardEvent) => void
  handleOnClick: (index: number) => void
  hasError: boolean
  autoFocus: boolean
} & Pick<PinInputProps, 'ariaLabel' | 'name' | 'pinLength'>

const isNumeric = (value: string) => {
  return /^\d+$/.test(value)
}

const PinInputBox = ({
  index,
  pin,
  inputRefs,
  autoTab,
  handleChange,
  handlePaste,
  handleOnClick,
  hasError,
  autoFocus,
  disabled = false,
  name,
  ariaLabel,
  pinLength,
}: PinInputBoxProps) => {
  return (
    <PinInputContainer onClick={() => handleOnClick(index)}>
      {pin[index] !== '' && <Circle data-testid="pincode-circle" />}
      <StyledInput
        ref={inputRefs[index]}
        aria-invalid={!!hasError}
        aria-label={`${ariaLabel}: PIN digit ${index + 1} of ${pinLength}`}
        data-testid={'pincode-input'}
        onChange={event => handleChange(event, index)}
        onPaste={event => handlePaste(event)}
        onKeyUp={event => autoTab(event, index)}
        hasError={hasError}
        autoFocus={autoFocus}
        value={pin[index]}
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={disabled}
        name={`${name}-${index}`}
        autoComplete="off"
      />
    </PinInputContainer>
  )
}

const PinInput = (props: PinInputProps) => {
  const {
    ariaLabel,
    disabled,
    isInvalid = false,
    isLoading = false,
    name,
    onChange,
    onComplete,
    pinLength,
    value,
  } = props
  const helpTextProps = extractHelpTextProps(props)
  const helpTextId = useHelpTextId(helpTextProps)
  const [errorAtIndex, setErrorAtIndex] = useState(-1)

  const [inputRefs] = useState(() => Array.from({ length: pinLength }, () => createRef<HTMLInputElement>()))
  const emptyArray = new Array(pinLength).fill('')
  const [pin, setPin] = useState(emptyArray)
  const pinCode = pin.join('').length === 0 ? '' : pin.join('')

  const updatePinCode = useCallback((newPin: Array<string>) => {
    setPin(newPin)
  }, [])

  // on error, we reset code value back to ''
  // When this happens, we want to reset the value of the pin
  if ((value !== undefined && pinCode !== value && value === '') || (pinCode === '' && !pin.every(val => val === ''))) {
    updatePinCode(emptyArray)
  }

  const passFocusToInput = useCallback(
    (index: number) => {
      /**
       * Pass focus to pin input at given index
       */
      if (inputRefs[index]) {
        inputRefs[index].current?.focus()

        setTimeout(() => {
          inputRefs[index].current?.setSelectionRange(0, 999)
        }, 0)
      }
    },
    [inputRefs]
  )

  // Remove the error so we can trigger the animation again
  useEffect(() => {
    if (errorAtIndex > -1) {
      const timer = setTimeout(() => {
        setErrorAtIndex(-1)
      }, ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
    return
  }, [errorAtIndex])

  // check to see if pin code is complete
  useEffect(() => {
    if (pinCode.length === pinLength && !/\s/g.test(pinCode)) {
      const timer = setTimeout(() => {
        passFocusToInput(0)
        if (onComplete) {
          onComplete(pinCode)
          setPin(emptyArray)
        }
      }, ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
    return
  }, [pinCode, emptyArray, onComplete, passFocusToInput, pinLength])

  const autoTab = useCallback(
    (event: KeyboardEvent, index: number) => {
      /** Handle switching focus based on key press events.
       *
       * Backspace Key or Delete Key ->
       *     - If current focus has a value, delete that value and NOT move focus to Left
       *     - If current focus does not have a value, move focus to Left and delete value in that Left box
       * Left Arrow Key -> Focus to the Left
       * Right Arrow Key or same key value that's already in that index-> Focus to the Right
       * Control, Meta, `v` -> Do nothing (override for pasting)
       * Numeric key -> Update current index value to key pressed and shift focus to Right
       * Non numeric key -> Set error
       */
      let nextActiveInput = -1

      if (event.key === 'Backspace' || event.key === 'Delete') {
        const newPin = [...pin]
        if (newPin[index]) {
          newPin[index] = ''
        } else {
          newPin[index - 1] = ''
          nextActiveInput = index - 1
        }
        updatePinCode(newPin)
        onChange?.(newPin.join(''))
      } else if (event.key === 'ArrowLeft') {
        nextActiveInput = index - 1
        // when a user re-enters the same value as the existing value in an input,
        // we shift focus to the right because the onChange handler is not called
      } else if (event.key === 'ArrowRight' || pinCode[index] === event.key) {
        nextActiveInput = index + 1
      } else if (event.key === 'Tab' || event.key === 'Meta' || event.key === 'Control' || event.key === 'v') {
        //do nothing
      } else if (isNumeric(event.key)) {
        // Don't update pin here - handleChange will handle it
        // handleChange also handles focus movement, so no need to set nextActiveInput
        return // Early return to prevent passFocusToInput from being called
      } else if (!isNumeric(event.key)) {
        setErrorAtIndex(index)
      }
      passFocusToInput(nextActiveInput)
    },
    [passFocusToInput, pin, updatePinCode, pinCode, onChange]
  )

  const handlePaste = (event: ClipboardEvent) => {
    /**
     * Handle a paste event inside a PinInput.
     * Conditions:
     * - paste content is not all numeric -> set an error
     * - paste content length < pinLength -> should fill in the entire pasted string
     * - paste content length > pinLength -> should fill in the first x digits of the pasted string where x is pinLength
     * - paste content length === pinLength -> fill the pin input and call onComplete
     *
     */
    event.preventDefault()
    const paste = event.clipboardData?.getData('text') ?? ''
    const pasteValue = paste.slice(0, pinLength)
    const focusIndex = Math.min(pinLength - 1, pasteValue.length)

    const isArrayNumeric = pasteValue.split('').every((value: string) => isNumeric(value))
    if (isArrayNumeric) {
      const newPin = pin.map((_, index) => {
        const value = pasteValue[index]
        return isNumeric(value) ? value : ''
      })
      updatePinCode(newPin)
      onChange?.(newPin.join(''))
      passFocusToInput(focusIndex)
    } else {
      updatePinCode(emptyArray)
      onChange?.(emptyArray.join(''))
      setErrorAtIndex(0)
    }
  }

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) => {
      /**
       * Handle when a value changes inside a PinInput
       *
       * If the value is numeric, update the pin with new value and move focus to the right.
       * Else, set an error at the given index.
       *
       * Note: If there is an existing value in the input, we override it with the new value
       */
      const value = event.currentTarget.value

      const newPin = [...pin]

      // if all the way to left input, we want to take the first char
      // else we want to take the last char
      const valueIndex = index === 0 ? 0 : value.length - 1
      newPin[index] = value[valueIndex]
      // validate that new input is numeric
      if (isNumeric(value)) {
        updatePinCode(newPin)
        onChange?.(newPin.join(''))
        passFocusToInput(index + 1)
      }
    },
    [pin, updatePinCode, passFocusToInput, onChange]
  )

  return (
    <Flex>
      {isLoading ? (
        <ProgressCircular size="large" data-testid="loading-icon" />
      ) : (
        <Grid>
          <Flex>
            {pin.map((_, index) => {
              return (
                <PinInputBox
                  name={name}
                  key={index}
                  ariaLabel={ariaLabel}
                  pinLength={pinLength}
                  pin={pin}
                  index={index}
                  inputRefs={inputRefs}
                  autoTab={autoTab}
                  handleChange={handleChange}
                  handlePaste={handlePaste}
                  hasError={isInvalid || errorAtIndex === index}
                  autoFocus={index === 0}
                  handleOnClick={passFocusToInput}
                  disabled={disabled}
                />
              )
            })}
          </Flex>
          <ArcadeHelpText {...helpTextProps} helpTextId={helpTextId} isInvalid={isInvalid} disabled={disabled} />
        </Grid>
      )}
    </Flex>
  )
}

export default PinInput
